import { useEffect, useMemo, useState } from "react";

import useSWR from "swr/immutable";

import { multiYotpoReviewsAPIFetcher } from "@react/hooks/fetchers";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  type ProductReviewsSettings,
  type YotpoReviewsAPIConnection,
} from "@root/tablescape/state";
import { ObjectKeys } from "@root/tablescape/utilities";

export type Score = "1" | "2" | "3" | "4" | "5";

export function useProductReviewsAggregate(_productIds: string[]) {
  const [productIds, setProductIds] = useState<string[]>([]);
  const {
    settings: { yotpoAppKey },
  } = useSectionSettings<ProductReviewsSettings>();

  useEffect(() => {
    if (process.env.STOREFRONT_ENV === "staging") {
      import("@root/scripts/product-id-map.json").then((productIdMap) => {
        setProductIds(
          _productIds.map((productId) => {
            const resolvedProductId = String(
              productId
            ) as keyof typeof productIdMap.staging.products;

            return String(
              productIdMap.staging.products[resolvedProductId]?.destinationId ??
                productId
            );
          })
        );
      });
    }

    setProductIds(_productIds.map((productId) => String(productId)));
  }, [_productIds]);

  const {
    data: productReviewsAggregate,
    isLoading: reviewsAggregateIsLoading,
  } = useSWR<YotpoReviewsAPIConnection[]>(
    productIds && yotpoAppKey
      ? productIds.map((productId) => [
          yotpoAppKey,
          productId,
          {
            per_page: "1",
          },
        ])
      : null,
    multiYotpoReviewsAPIFetcher
  );

  const aggregateReviewCount = useMemo<number | undefined>(() => {
    if (reviewsAggregateIsLoading) {
      return undefined;
    }

    return productReviewsAggregate?.reduce(
      (sum, reviewData) =>
        (sum += reviewData?.response.bottomline.total_review),
      0
    );
  }, [reviewsAggregateIsLoading, productReviewsAggregate]);

  const aggregateReviewScore = useMemo<string | undefined>(() => {
    if (reviewsAggregateIsLoading || !productReviewsAggregate) {
      return undefined;
    }

    return (
      productReviewsAggregate.reduce(
        (sum, reviewData) =>
          (sum += reviewData?.response.bottomline.average_score),
        0
      ) / productIds.length
    ).toFixed(1);
  }, [reviewsAggregateIsLoading, productReviewsAggregate, productIds.length]);

  const aggregateStarDistribution = useMemo(() => {
    if (reviewsAggregateIsLoading) {
      return undefined;
    }

    const starDistribution = {
      "1": 0,
      "2": 0,
      "3": 0,
      "4": 0,
      "5": 0,
    };

    productReviewsAggregate?.forEach((reviewData) => {
      const starDistributionData =
        reviewData?.response.bottomline.star_distribution;
      if (starDistributionData) {
        ObjectKeys(starDistribution).forEach((key) => {
          starDistribution[key] += starDistributionData[key] ?? 0;
        });
      }
    });

    return starDistribution;
  }, [reviewsAggregateIsLoading, productReviewsAggregate]);

  return {
    aggregateReviewCount,
    aggregateReviewScore,
    aggregateStarDistribution,
    reviewsAggregateIsLoading,
  };
}
